import React, {useCallback, useEffect} from 'react';
import {View, StyleSheet, ViewStyle, TextStyle} from 'react-native';

import {ResetPassword} from 'components/common/reset-password/ResetPassword';
import {spacing} from 'theme/Units';

import {NativeStackScreenProps} from '@react-navigation/native-stack';
import {RootNavigatorParams, Routes} from 'navigation/Routes';

import {useDispatch, useSelector} from 'react-redux';
import {
  getAuthError,
  getAuthSuccess,
  recoverEmail,
  resetPasswordCheck,
} from 'redux/selectors/authSelector';
import {useToast} from 'hooks';
import {ERROR_KEY, SUCCESS_KEY} from 'constants/keys';
import {
  ErrorCodes,
  getErrorMessage,
  getSuccessMessage,
  SuccessCodes,
} from 'utils/getMessages';
import {
  resetMessage,
  resetPasswordRequest,
  setNewPasswordRequest,
  setIsPasswordUpdated,
} from 'redux/slices/authSlice';
import {useResponsiveStyles} from 'hooks/useResponsiveStyles';
import {GradientWithLogo} from 'components/auth-screen/GradientWithLogo';

type propsType = Readonly<{
  mode: 'reset' | 'forgot';
}> &
  NativeStackScreenProps<RootNavigatorParams>;
  
export function ResetPasswordScreen(props: propsType) {
  const {navigation, mode} = props;
  const dispatch = useDispatch();
  const resetPasswordSuccess = useSelector(getAuthSuccess);
  const resetPasswordFailure = useSelector(getAuthError);
  const {showToasts} = useToast();
  const canResetPassword = useSelector(resetPasswordCheck);
  const emailToRecover = useSelector(recoverEmail);
  const styles = useResponsiveStyles<styleTypes>(baseStyles, responsive);

  const goBackToLoginPage = useCallback(() => {navigation.navigate(Routes.LOGIN)},[navigation])

  type initialValuesType = {
    current_password: string;
    new_password: string;
    confirm_password: string;
  };
  const initialValues: initialValuesType = {
    current_password: '',
    new_password: '',
    confirm_password: '',
  };

  function handleResetPassword({
    confirm_password,
    ...values
  }: initialValuesType) {
    if (mode === 'reset') {
      dispatch(resetPasswordRequest(values));
    } else {
      dispatch(
        setNewPasswordRequest({
          email: emailToRecover,
          new_password: values.new_password,
        }),
      );
    }
  }

  //useeffect for success message and dispatch setIsPasswordUpdated to make it true
  useEffect(() => {
    if (resetPasswordSuccess) {
      if (resetPasswordSuccess === 'S-10072') {
        showToasts(
          SUCCESS_KEY,
          getSuccessMessage(resetPasswordSuccess as SuccessCodes),
          'success',
        );
        dispatch(setIsPasswordUpdated(true));
        // navigate to login screen
        if (mode === 'forgot'){
          goBackToLoginPage();
        }
      }
      dispatch(resetMessage());
    }
  }, [dispatch, resetPasswordSuccess, showToasts, goBackToLoginPage,mode]);

  //useeffect for error message
  useEffect(() => {
    if (resetPasswordFailure) {
      showToasts(
        ERROR_KEY,
        getErrorMessage(resetPasswordFailure as ErrorCodes),
        'error',
      );
      dispatch(resetMessage());
    }
  }, [dispatch, resetPasswordFailure, showToasts]);

  function handleCancel() {
    if (mode === 'forgot') {
      goBackToLoginPage();
    }
  }

  useEffect(() => {
    // in forgot mode id the user open this screen without doing forgot password steps take him back to login screen
    if (mode === 'forgot' && !canResetPassword) {
      goBackToLoginPage()
    }
  }, [mode, goBackToLoginPage, canResetPassword]);

  return (
    <View style={styles.container}>
      <GradientWithLogo style={styles.imageContainer} />

      <View style={styles.secondSection}>
        <ResetPassword
          mode={mode}
          header="Reset password to proceed"
          image={require('assets/illustrations/password.png')}
          headerStyle={styles.headerStyle}
          onSubmit={handleResetPassword}
          initialValues={initialValues}
          onCancel={handleCancel}
        />
      </View>
    </View>
  );
}

type styleTypes = {
  container: ViewStyle;
  secondSection: ViewStyle;
  headerStyle: TextStyle;
  imageContainer: ViewStyle;
};
const baseStyles: styleTypes = StyleSheet.create({
  imageContainer: {},
  headerStyle: {fontSize: 16},
  container: {
    display: 'grid',
    gridTemplateColumns: '1fr 1fr',
    flex: 1,
  },
  secondSection: {
    width: '100%',
    backgroundColor: 'white',
    justifyContent: 'center',
    alignItems: 'center',
    padding: spacing['4'],
  },
});

const responsive: Record<number, Partial<styleTypes>> = {
  640: StyleSheet.create({
    container: {
      display: 'flex',
      flexDirection: 'column',
      justifyContent: 'center',
      alignItems: 'center',
    },
    imageContainer: {
      display: 'none',
    },
  }),
  480: StyleSheet.create({
    imageContainer: {
      display: 'none',
    },
  }),
};
